import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  LayoutGrid,
  List,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { posts } from "../data/siteContent";
import { apiRequest } from "../lib/api";

const POSTS_PER_PAGE = 6;

const postImages = {
  "a-better-way-to-execute-in-nigeria": {
    src: "/brand/bridge-banner.jpeg",
    position: "object-center",
  },
  "the-five-checks-before-buying-a-car-abroad": {
    src: "/brand/bridge-executive-team.png",
    position: "object-center",
  },
  "what-milestone-payments-protect": {
    src: "/brand/bridge-byva-logo.jpg",
    position: "object-center",
  },
};

const fallbackImage = {
  src: "/brand/bridge-banner.jpeg",
  position: "object-center",
};

export function Blog() {
  const [page, setPage] = useState(1);
  const [view, setView] = useState("grid");
  const [remotePosts, setRemotePosts] = useState(null);
  useEffect(() => { let active = true; apiRequest('/public/blog?limit=24').then((response) => { if (active && response.data?.length) setRemotePosts(response.data.map((post) => ({ ...post, date: post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : '', readTime: post.readTime || '5 min read', category: post.category || 'Insights' }))); }).catch(() => {}); return () => { active = false; }; }, []);
  const orderedPosts = [...(remotePosts || posts)].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const pageCount = Math.max(
    1,
    Math.ceil(orderedPosts.length / POSTS_PER_PAGE),
  );
  const visiblePosts = orderedPosts.slice(
    (page - 1) * POSTS_PER_PAGE,
    page * POSTS_PER_PAGE,
  );

  const changePage = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <>
      <PageHero
        eyebrow="Insights"
        title="Useful thinking for moving with confidence."
        copy="Practical notes on local execution, trust, operations and the details that make projects work."
      />
      <section id="content" className="section-space bg-mist">
        <div className="container">
          <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="eyebrow">The Bridge journal</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-[-.04em] text-ink sm:text-4xl">
                Ideas for better execution.
              </h2>
            </div>
            <div
              className="inline-flex self-start rounded-full border border-ink/10 bg-white p-1 shadow-sm sm:self-auto"
              role="group"
              aria-label="Choose blog view"
            >
              <button
                type="button"
                onClick={() => setView("grid")}
                aria-pressed={view === "grid"}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition ${view === "grid" ? "bg-ink text-white" : "text-slate hover:text-ink"}`}
              >
                <LayoutGrid size={15} />
                <span>Grid</span>
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                aria-pressed={view === "list"}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition ${view === "list" ? "bg-ink text-white" : "text-slate hover:text-ink"}`}
              >
                <List size={15} />
                <span>List</span>
              </button>
            </div>
          </div>

          <div
            className={
              view === "grid"
                ? "grid gap-6 md:grid-cols-2 lg:grid-cols-3"
                : "space-y-5"
            }
          >
            {visiblePosts.map((post, index) => {
              const image = post.coverImageUrl ? { src: post.coverImageUrl, position: "object-center" } : postImages[post.slug] || fallbackImage;
              const featured = page === 1 && index === 0;

              return (
                <Reveal key={post.slug} delay={index * 70}>
                  <article
                    className={`group h-full overflow-hidden rounded-[1.75rem] border border-ink/10 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-soft ${view === "list" ? "md:min-h-[220px]" : ""}`}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className={`flex h-full ${view === "grid" ? "flex-col" : "flex-col md:flex-row"}`}
                    >
                      <div
                        className={`relative overflow-hidden bg-ink ${view === "grid" ? "aspect-[16/10]" : "aspect-[16/10] md:aspect-auto md:min-h-[220px] md:w-[280px] md:shrink-0"}`}
                      >
                        <img
                          src={image.src}
                          alt=""
                          className={`h-full w-full object-cover transition duration-700 group-hover:scale-105 ${image.position}`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/5 to-transparent" />
                        <div className="absolute inset-x-5 bottom-5 flex items-center justify-between gap-3 text-[10px] font-extrabold uppercase tracking-[.16em] text-white">
                          <span>{post.category}</span>
                          {featured && (
                            <span className="rounded-full bg-bridge px-3 py-1.5 text-ink">
                              Featured
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-1 flex-col p-6">
                        <div className="flex items-center justify-between gap-4 text-xs text-slate">
                          <span>{post.date}</span>
                          <span>{post.readTime}</span>
                        </div>
                        <h2 className="mt-4 font-display text-xl font-bold leading-tight tracking-[-.03em] text-ink transition group-hover:text-ocean">
                          {post.title}
                        </h2>
                        <p className="mt-3 flex-1 text-sm leading-7 text-slate">
                          {post.excerpt}
                        </p>
                        <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ocean">
                          Read article
                          <ArrowUpRight
                            size={16}
                            className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                          />
                        </span>
                      </div>
                    </Link>
                  </article>
                </Reveal>
              );
            })}
          </div>

          {pageCount > 1 && (
            <nav
              className="mt-12 flex items-center justify-center gap-2"
              aria-label="Blog pagination"
            >
              <button
                type="button"
                onClick={() => changePage(page - 1)}
                disabled={page === 1}
                aria-label="Previous page"
                className="grid h-11 w-11 place-items-center rounded-full border border-ink/10 bg-white text-ink transition hover:border-ocean hover:text-ocean disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowLeft size={17} />
              </button>
              {Array.from({ length: pageCount }, (_, index) => index + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => changePage(pageNumber)}
                    aria-current={pageNumber === page ? "page" : undefined}
                    className={`grid h-11 min-w-[2.75rem] place-items-center rounded-full px-3 text-sm font-bold transition ${pageNumber === page ? "bg-ink text-white" : "border border-ink/10 bg-white text-ink hover:border-ocean hover:text-ocean"}`}
                  >
                    {pageNumber}
                  </button>
                ),
              )}
              <button
                type="button"
                onClick={() => changePage(page + 1)}
                disabled={page === pageCount}
                aria-label="Next page"
                className="grid h-11 w-11 place-items-center rounded-full border border-ink/10 bg-white text-ink transition hover:border-ocean hover:text-ocean disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowRight size={17} />
              </button>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}
